import React, { useEffect, useRef, useState } from "react";
import RecordingManager from "../recording/RecordingManager";
import "./ExternalWebGameLauncher.scss";

/**
 * External Web Game Launcher
 * For sites that block iframe embedding (X-Frame-Options / CSP frame-ancestors).
 * Starts Immersely recording, then opens the game in a new browser tab.
 */
function ExternalWebGameLauncher({ assignment, onClose, onError, gameUrl: gameUrlProp }) {
  const recordingManagerRef = useRef(null);
  const screenCaptureStreamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [localError, setLocalError] = useState(null);

  const gameUrl = gameUrlProp || assignment?.webUrl || "";

  const notifyError = (message) => {
    setLocalError(message);
    if (onError) {
      onError(message);
    }
  };

  const openGame = () => {
    if (!gameUrl) {
      notifyError("Game URL is missing for this assignment.");
      return;
    }
    // Do not pass "noopener" in the features string — browsers then return null
    // even when the tab opens successfully, which falsely triggers "popup blocked".
    const opened = window.open(gameUrl, "_blank");
    if (!opened) {
      notifyError("Popup blocked. Allow popups for Immersely, then click Open Game Again.");
      return;
    }
    try {
      opened.opener = null;
    } catch (_) {
      // Ignore cross-origin / browser restrictions on clearing opener.
    }
  };

  const clearScreenCaptureStream = () => {
    if (screenCaptureStreamRef.current) {
      screenCaptureStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    screenCaptureStreamRef.current = null;
  };

  const ensureScreenCapturePermission = async () => {
    if (screenCaptureStreamRef.current) {
      const track = screenCaptureStreamRef.current.getVideoTracks?.()[0];
      if (track && track.readyState === "live") {
        return;
      }
      clearScreenCaptureStream();
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        mediaSource: "screen",
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const videoTrack = stream.getVideoTracks?.()[0];
    const displaySurface = videoTrack?.getSettings?.().displaySurface;

    // External browser games need Entire Screen or Window — tab capture stops when switching tabs.
    if (displaySurface === "browser") {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error(
        "Please share an entire screen or app window (not a browser tab) so gameplay in the other tab can be recorded."
      );
    }

    screenCaptureStreamRef.current = stream;

    if (videoTrack) {
      videoTrack.onended = () => {
        clearScreenCaptureStream();
      };
    }
  };

  const startSession = async () => {
    if (!recordingManagerRef.current || isRecording || recordingStatus === "starting") {
      return;
    }

    setLocalError(null);
    setRecordingStatus("starting");
    try {
      await ensureScreenCapturePermission();
      await recordingManagerRef.current.start();
      setRecordingStatus("recording");
      setIsRecording(true);
      openGame();
    } catch (err) {
      const message = err?.message || "Failed to start session recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const stopSession = async () => {
    if (
      !recordingManagerRef.current ||
      !isRecording ||
      recordingStatus === "stopping"
    ) {
      return;
    }

    setRecordingStatus("stopping");
    try {
      await recordingManagerRef.current.stop();
      setIsRecording(false);
      setRecordingStatus("stopped");
      clearScreenCaptureStream();
    } catch (err) {
      const message = err?.message || "Failed to stop session recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const handleClose = async () => {
    try {
      if (isRecording) {
        await stopSession();
      }
    } finally {
      if (onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (recordingManagerRef.current) {
        recordingManagerRef.current.stop().catch(() => {});
      }
      clearScreenCaptureStream();
    };
  }, []);

  return (
    <div className="external-web-game-launcher">
      <RecordingManager
        ref={recordingManagerRef}
        assignmentId={assignment.assignmentId || assignment._id}
        gameId={assignment.gameId}
        username={assignment.username}
        getScreenCaptureStream={() => screenCaptureStreamRef.current}
        onRecordingStarted={() => {
          setIsRecording(true);
          setRecordingStatus("recording");
        }}
        onRecordingStopped={() => {
          setIsRecording(false);
          setRecordingStatus("stopped");
        }}
        onError={(message) => {
          setRecordingStatus("error");
          notifyError(message);
        }}
      />

      <div className="external-launcher-panel">
        <h3>{assignment?.gameName || "Web Game"} Session</h3>
        <p className="external-launcher-subtitle">
          This game blocks embedding in Immersely. Start recording here, then play in a new browser tab.
          Return to this window to end your session.
        </p>

        <div className="external-instructions">
          <p>1. Click Start Session (camera/mic/screen permission prompts will appear).</p>
          <p>2. In the screen share picker, choose Entire Screen or a Window (not Browser Tab).</p>
          <p>3. The game opens in a new tab. Play as normal.</p>
          <p>4. Return here and click End Session when done.</p>
        </div>

        <div className="external-status-row">
          <span className={`status-dot ${isRecording ? "recording" : ""}`} />
          <span className="status-text">
            {isRecording
              ? "Recording"
              : recordingStatus === "starting"
              ? "Starting..."
              : recordingStatus === "stopping"
              ? "Stopping..."
              : "Not Recording"}
          </span>
        </div>

        {localError && <div className="external-error">{localError}</div>}

        <div className="external-actions">
          {!isRecording ? (
            <button
              className="btn btn-primary"
              onClick={startSession}
              disabled={recordingStatus === "starting"}
            >
              Start Session & Open Game
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={stopSession}
              disabled={recordingStatus === "stopping"}
            >
              {recordingStatus === "stopping" ? "Ending Session..." : "End Session"}
            </button>
          )}

          <button className="btn btn-secondary" onClick={openGame} disabled={!gameUrl}>
            Open Game Again
          </button>

          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExternalWebGameLauncher;
